/**
 * How a booking / quote / attendee reached us. Stored on each booking,
 * quote, and course session attendee row so the same customer can have
 * different sources over time. The CRM record derives a "First seen via"
 * from the earliest non-null source via the `v_customer_first_seen` view.
 *
 * `bulk_import` is set automatically for paste-and-parse rows.
 */
export type InboundChannel =
  | 'email'
  | 'phone'
  | 'whatsapp'
  | 'website'
  | 'walk_in'
  | 'bulk_import'
  | 'returning_client'
  | 'other';

/** Ordered options for the Source dropdown in booking/quote/attendee modals. */
export const INBOUND_CHANNEL_OPTIONS: ReadonlyArray<{ value: InboundChannel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'website', label: 'Website' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'returning_client', label: 'Returning client' },
  { value: 'bulk_import', label: 'Bulk import' },
  { value: 'other', label: 'Other' },
];

/** Lookup for displaying a stored InboundChannel value back as a label. */
export function inboundChannelLabel(value: InboundChannel | string | null | undefined): string {
  if (!value) return '—';
  const match = INBOUND_CHANNEL_OPTIONS.find((o) => o.value === value);
  return match ? match.label : String(value);
}

export interface CertificateData {
  attendeeName: string;
  companyName: string;
  courseName: string;
  courseDate: string;
  courseDates?: string[]; // Optional array for multiple dates (Order Picker, VNA)
  instructorName?: string;
  truckType?: string;
  truckMake?: string;
  truckCapacity?: string;
  truckAttachments?: string;
  instructorRegNumber?: string;
}

interface FieldConfig {
  label: string;
  dataColumn: string;
  defaultValue: string;
}

interface FieldGroupConfig {
  requiredFor: string[];
  fields: FieldConfig[];
  placement: 'below-completion-statement' | 'below-instructor-name';
}

export const CERTIFICATE_FIELD_CONFIG = {
  liftTruckFields: {
    requiredFor: [
      'Lift Truck Operator',
      'Lift Truck Operator - Counterbalance',
      'Lift Truck Operator - Counterbalance (REF)',
      'Lift Truck Operator - Counterbalance (NOV)',
      'Lift Truck Operator - Counterbalance (Conversion)',
      'Lift Truck Operator - Conversion - Counterbalance',
      'Lift Truck Operator - LLOP (Low-Level Order Picker)',
      'Lift Truck Operator - PPT',
      'Lift Truck Operator - PPT (Ride On)',
      'Lift Truck Operator - PPT (Walk With)',
      'Lift Truck Operator - Reach',
      'Lift Truck Operator - Reach (REF)',
      'Lift Truck Operator - Reach (NOV)',
      'Lift Truck Operator - Conversion - Reach',
      'Lift Truck Operator - Stacker',
      'Lift Truck Operator - Multidirectional Reach',
      'Lift Truck Operator - Conversion - Multidirectional Reach',
      'Lift Truck Operator - Conversion - Stacker',
      'VNA Truck (Very Narrow Aisle)',
      'Order Picker',
      'CSCS Telescopic Handler',
      'NPORS Telescopic Handler all sizes including 360° slew',
      'Telescopic Handler / Teleporter Training',
      'Power Pallet Instructor Training',
      'Power Pallet Instructor',
      'Pivot Steer'
    ],
    fields: [
      {
        label: 'TRUCK TYPE:',
        dataColumn: 'truckType',
        defaultValue: 'N/A'
      },
      {
        label: 'MAKE:',
        dataColumn: 'truckMake',
        defaultValue: 'N/A'
      },
      {
        label: 'CAPACITY:',
        dataColumn: 'truckCapacity',
        defaultValue: 'N/A'
      },
      {
        label: 'ATTACHMENTS:',
        dataColumn: 'truckAttachments',
        defaultValue: 'N/A'
      }
    ],
    placement: 'below-completion-statement'
  },
  mewpFields: {
    requiredFor: ['MEWP Operator', 'MEWP – Spider Lift Operator'],
    fields: [
      {
        label: 'TRUCK TYPE:',
        dataColumn: 'truckType',
        defaultValue: 'N/A'
      }
    ],
    placement: 'below-completion-statement'
  },
  instructorRegNumber: {
    requiredFor: [
      'Lift Truck Operator',
      'Lift Truck Operator - Counterbalance',
      'Lift Truck Operator - Counterbalance (REF)',
      'Lift Truck Operator - Counterbalance (NOV)',
      'Lift Truck Operator - Counterbalance (Conversion)',
      'Lift Truck Operator - Conversion - Counterbalance',
      'Lift Truck Operator - LLOP (Low-Level Order Picker)',
      'Lift Truck Operator - PPT',
      'Lift Truck Operator - PPT (Ride On)',
      'Lift Truck Operator - PPT (Walk With)',
      'Lift Truck Operator - Reach',
      'Lift Truck Operator - Reach (REF)',
      'Lift Truck Operator - Reach (NOV)',
      'Lift Truck Operator - Conversion - Reach',
      'Lift Truck Operator - Stacker',
      'Lift Truck Operator - Multidirectional Reach',
      'Lift Truck Operator - Conversion - Multidirectional Reach',
      'Lift Truck Operator - Conversion - Stacker',
      'VNA Truck (Very Narrow Aisle)',
      'Order Picker',
      'Banksman',
      'Gantry Crane',
      'MEWP Operator',
      'MEWP – Spider Lift Operator',
      'Power Pallet Instructor Training',
      'Power Pallet Instructor',
      'Pivot Steer'
    ],
    fields: [
      {
        label: 'REG NUMBER:',
        dataColumn: 'instructorRegNumber',
        defaultValue: ''
      }
    ],
    placement: 'below-instructor-name'
  }
} as const;

export interface ProcessedCertificate extends CertificateData {
  id: string;
  fileName: string;
  locationId?: string; // Location ID from booking or certificate submission for matching in client portal
  /** Set for certs created from a booking; used to look up test/practical status */
  bookingId?: string;
  attendeeId?: string;
  /** Stored CRM expiry (YYYY-MM-DD); preferred on the certificate face when set */
  expiryDate?: string;
  /** True when this attendee passed the assigned test (80%+ or course pass %) */
  testPassed?: boolean;
  /** True when instructor marked practical as passed for this attendee */
  practicalPassed?: boolean;
  /** Origin of the record: public form (QR) or CSV upload; used for UI badges */
  source?: 'public_form' | 'csv';
}

export const COURSE_CONFIG = {
  'Abrasive Wheels': {
    displayNameOnCertificate: 'ABRASIVE WHEELS\nCERTIFICATE OF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN ABRASIVE WHEELS TRAINING COURSE',
    additionalStaticText: [
      'ACCIDENTS, INSPECTION',
      'REQUIREMENTS OF THE REGULATIONS, TRANSPORTING, STORAGE AND HANDLING',
      'MARKINGS, MOUNTING, DRESSING AND ADJUSTMENTS',
      'STRUCTURING AND DELIVERY, PRE USE CHECKS'
    ],
    validityMonths: 36,
    templateType: 'AW',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'Banksman': {
    displayNameOnCertificate: 'BANKSMAN TRANING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A BANKSMAN TRAINING COURSE',
    validityMonths: 36,
    templateType: 'BM',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Community First Respond (CFR)': {
    displayNameOnCertificate: 'COMMUNITY FIRST RESPOND\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A COMMUNITY FIRST RESPOND COURSE',
    complianceStatement: 'COMMUNITY FIRST RESPOND COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'CFR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'Emergency First Aid': {
    displayNameOnCertificate: 'CERTIFICATE OF COMPETENCY\nIN\nEMERGENCY FIRST AID',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN EMERGENCY FIRST AID COURSE',
    complianceStatement: 'EMERGENCY FIRST AID COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'EFA',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Epilepsy Awareness': {
    displayNameOnCertificate: 'CERTIFICATE OF COMPETENCY\nIN\nEPILEPSY AWARENESS',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN EPILEPSY AWARENESS COURSE',
    complianceStatement: 'EPILEPSY AWARENESS COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'EPILEPSY',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Emergency Response Team': {
    displayNameOnCertificate: 'CERTIFICATE OF COMPETENCY\nIN\nEMERGENCY RESPONSE TEAM',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN EMERGENCY RESPONSE TEAM COURSE',
    complianceStatement: 'EMERGENCY FIRST AID COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'ERT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Confined Space Training': {
    displayNameOnCertificate: 'CONFINED SPACE TRAINING\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A CONFINED SPACE TRAINING COURSE',
    complianceStatement: 'CONFINED SPACE TRAINING IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'CONFINED_SPACE',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    defaultInstructor: 'John Smith'
  },
  'Fire Safety and Extinguisher Use': {
    displayNameOnCertificate: 'CERTIFICATE OF ATTENDANCE\nFIRE SAFETY AWARENESS & USE OF PORTABLE FIRE EXTINGUISHERS',
    completionStatement: 'HAS ATTENDED A COURSE ON FIRE SAFETY AWARENESS AND THE PRACTICAL USE OF PORTABLE FIRE EXTINGUISHERS',
    complianceStatement: '',
    validityMonths: 24,
    templateType: 'FIRE_SAFETY',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    defaultInstructor: 'John Smith'
  },
  'Fire Safety Awareness': {
    displayNameOnCertificate: 'CERTIFICATE OF ATTENDANCE\nFIRE SAFETY AWARENESS',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FIRE SAFETY AWARENESS COURSE',
    complianceStatement: 'FIRE SAFETY AWARENESS COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'FIRE_SAFETY_AWARENESS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    defaultInstructor: 'John Smith'
  },
  'Fire Warden': {
    displayNameOnCertificate: 'FIRE WARDEN CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A FIRE WARDEN TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'FW',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'First Aid Responder Novice (FAR NOV)': {
    displayNameOnCertificate: 'FIRST AID RESPONDER NOVICE\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FIRST AID RESPONDER NOVICE COURSE',
    complianceStatement: 'FIRST AID RESPONDER NOVICE COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'FAR_NOV',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'First Aid Responder Refresher (FAR REF)': {
    displayNameOnCertificate: 'FIRST AID RESPONDER REFRESHER\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FIRST AID RESPONDER REFRESHER COURSE',
    complianceStatement: 'FIRST AID RESPONDER REFRESHER COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'FAR_REF',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'First Aid Workshop Session': {
    displayNameOnCertificate: 'FIRST AID WORKSHOP SESSION\nCERTIFICATE\nOF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FIRST AID WORKSHOP SESSION COURSE',
    complianceStatement: 'FIRST AID WORKSHOP SESSION COURSE IS IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 24,
    templateType: 'FIRST_AID_WORKSHOP',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Gantry Crane': {
    displayNameOnCertificate: 'GANTRY CRANE CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A GANTRY CRANE TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR GANTRY CRANE: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'GC',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Gator': {
    displayNameOnCertificate: 'CERTIFICATE OF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A GATOR TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'GATOR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'Hot Works & Fire Watch': {
    displayNameOnCertificate: 'HOT WORKS & FIRE WATCH CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A HOT WORKS & FIRE WATCH COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'HOT_WORKS_FIRE_WATCH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'Francis Collier'
  },
  'IPAF (1a) Static Vertical': {
    displayNameOnCertificate: 'IPAF OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF STATIC VERTICAL TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_1A',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF (1b) Static Boom': {
    displayNameOnCertificate: 'IPAF OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF STATIC BOOM TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_1B',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF (3a & 3b) Mobile Vertical & Mobile Boom': {
    displayNameOnCertificate: 'IPAF OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF MOBILE VERTICAL & MOBILE BOOM TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_3A_3B',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF (3a only) Mobile Vertical': {
    displayNameOnCertificate: 'IPAF OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF MOBILE VERTICAL TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_3A',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF (3b only) Mobile Boom': {
    displayNameOnCertificate: 'IPAF OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF MOBILE BOOM TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_3B',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF MEWP for Managers': {
    displayNameOnCertificate: 'IPAF MEWP FOR MANAGERS\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF MEWP FOR MANAGERS TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_MANAGERS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'IPAF Harness (WAH)': {
    displayNameOnCertificate: 'IPAF HARNESS (WAH)\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN IPAF HARNESS (WORKING AT HEIGHT) TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE INTERNATIONAL POWERED ACCESS FEDERATION',
    validityMonths: 60,
    templateType: 'IPAF_HARNESS_WAH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Counterbalance (NOV)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_CB_NOV',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Counterbalance (REF)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_CB',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Counterbalance (Conversion)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_CB',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Conversion - Counterbalance': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_CB',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Multidirectional Reach': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Conversion - Multidirectional Reach': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - PPT (Ride On)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_PPT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - PPT (Walk With)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_PPT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Reach (NOV)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_REACH_NOV',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Reach (REF)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_REACH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Conversion - Reach': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_REACH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Stacker': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - LLOP (Low-Level Order Picker)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'VEHICLE/ LORRY MOUNTED CRANE (HIAB)': {
    displayNameOnCertificate: 'VEHICLE / LORRY MOUNTED CRANE (HIAB)\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A VEHICLE / LORRY MOUNTED CRANE (HIAB) TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'HIAB_CRANE',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - Conversion - Stacker': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Manual Handling': {
    displayNameOnCertificate: 'MANUAL HANDLING CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A MANUAL HANDLING TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'MH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'Manual Handling Instructor': {
    displayNameOnCertificate: 'MANUAL HANDLING INSTRUCTOR\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A MANUAL HANDLING INSTRUCTOR TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 60,
    templateType: 'MH_INSTRUCTOR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'MEWP Operator': {
    displayNameOnCertificate: 'MEWP OPERATOR CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A MEWP TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED MEWP: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'MEWP',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'MEWP – Spider Lift Operator': {
    displayNameOnCertificate: 'MEWP – SPIDER LIFT OPERATOR\nCERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A MEWP SPIDER LIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED MEWP: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'MEWP_SPIDER',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Portable Appliance Testing': {
    displayNameOnCertificate: 'CERTIFICATE OF TRAINING\nIN\nPORTABLE APPLIANCE TESTING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A PORTABLE APPLIANCE TESTING COURSE',
    complianceStatement: 'This training is in accordance with the 2005 Safety, Health & Welfare at Work Act and all other Regulations, Codes of Practice and HSA Guidelines that are relevant and applicable for Portable Appliance Testing Training.',
    validityMonths: 60,
    templateType: 'PAT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Power Pallet Instructor': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A POWER PALLET INSTRUCTOR TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'POWER_PALLET_INSTRUCTOR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'Gent Peci'
  },
  'Working at Height': {
    displayNameOnCertificate: 'WORKING AT HEIGHT CERTIFICATE OF\nATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A WORKING AT HEIGHT TRAINING COURSE',
    additionalStaticText: [
      'THE LAW REGARDING WORK AT HEIGHT - HOW TO SELECT CORRECT FALL PROTECTION',
      'FALL ARREST SYSTEMS VS RESTRAINT SYSTEMS - DAILY INSPECTION CHECKS',
      'HARNESS USE INSPECTIONS, SELECTION & FITTING - LADDER SAFETY / RESCUE PLANS',
      'AND IS IN ACCORDANCE WITH THE CONSTRUCTION REGULATIONS'
    ],
    validityMonths: 36,
    templateType: 'WAH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR : ',
    defaultInstructor: 'John Smith'
  },
  'Working at Height Instructor': {
    displayNameOnCertificate: 'WORKING AT HEIGHT INSTRUCTOR\nCERTIFICATE OF COMPETENCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A WORKING AT HEIGHT INSTRUCTOR TRAINING COURSE',
    additionalStaticText: [
      'QUALIFIED TO DELIVER WORKING AT HEIGHT TRAINING',
      'FALL PROTECTION SYSTEMS - EQUIPMENT INSPECTION - RISK ASSESSMENT',
      'EMERGENCY PROCEDURES - RESCUE PLANNING - LEGISLATIVE REQUIREMENTS'
    ],
    complianceStatement: 'AND IS IN ACCORDANCE WITH THE CONSTRUCTION REGULATIONS',
    validityMonths: 36,
    templateType: 'WAH_INSTRUCTOR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR : ',
    defaultInstructor: 'John Smith'
  },
  'Working at Height Awareness': {
    displayNameOnCertificate: 'CERTIFICATE OF ATTENDANCE\nWORKING AT HEIGHT AWARENESS',
    completionStatement: 'HAS ATTENDED A COURSE ON WORKING AT HEIGHT AWARENESS',
    complianceStatement: '',
    validityMonths: 36,
    templateType: 'WAH_AWARENESS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'Lift Truck Operator - PPT': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_PPT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'Pivot Steer': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A FORKLIFT TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_PIVOT_STEER',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'Gent Peci'
  },
  'Safepass Training': {
    displayNameOnCertificate: 'SAFEPASS TRAINING\nCERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A SAFEPASS TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 48,
    templateType: 'SAFEPASS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith',
    /** Default course fee for Add to session / workspace (overridable per booking); Super Admin can also set via course products DB. */
    defaultPrice: 200
  },
  'Safety Representative Training': {
    displayNameOnCertificate: 'SAFETY REPRESENTATIVE TRAINING\nCERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A SAFETY REPRESENTATIVE TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'SRT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'VNA Truck (Very Narrow Aisle)': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_VNA',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'John Smith'
  },
  'VDU / DSE HOME WORKING RISK ASSESSOR TRAINING COURSE': {
    displayNameOnCertificate: 'CERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS ATTENDED VDU / DSE HOME WORKING RISK ASSESSOR TRAINING COURSE ON BEHALF OF',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2007',
    validityMonths: 60,
    templateType: 'VDU_DSE',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    instructorLabelOverride: 'NAME OF INSTRUCTOR:',
    defaultInstructor: 'John Smith'
  },
  'CSCS Telescopic Handler': {
    displayNameOnCertificate: 'CSCS TELESCOPIC HANDLER\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A CSCS TELESCOPIC HANDLER TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH',
    validityMonths: 60,
    templateType: 'CSCS_TH',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Chemical Safety & Spills Response Training': {
    displayNameOnCertificate: 'CHEMICAL SAFETY & SPILLS RESPONSE TRAINING\nCERTIFICATE\nOF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A CHEMICAL SAFETY & SPILLS RESPONSE TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005.\nThis course equips participants with the essential knowledge and skills to handle, store, and dispose of hazardous chemicals safely, and to respond effectively to chemical spills in the workplace.',
    validityMonths: 36,
    templateType: 'CHEMICAL_SAFETY_SPILLS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName'],
    defaultInstructor: 'Francis Collier'
  },
  'Facility Hire & Other Services': {
    displayNameOnCertificate: 'FACILITY HIRE & OTHER SERVICES\nCERTIFICATE\nOF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A FACILITY HIRE & OTHER SERVICES COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'FACILITY_HIRE',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'Certs Production Cost': {
    displayNameOnCertificate: 'CERTS PRODUCTION COST\nCERTIFICATE\nOF ATTENDANCE',
    completionStatement: 'HAS ATTENDED A CERTS PRODUCTION COST COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'CERTS_PRODUCTION',
    requiredFields: ['attendeeName', 'companyName', 'courseDate'],
    defaultInstructor: 'John Smith'
  },
  'NPORS Crane/Lift Supervisor': {
    displayNameOnCertificate: 'NPORS CRANE/LIFT SUPERVISOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN NPORS CRANE/LIFT SUPERVISOR TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL PLANT OPERATORS REGISTRATION SCHEME (NPORS)',
    validityMonths: 36,
    templateType: 'NPORS_CRANE_LIFT_SUPERVISOR',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'NPORS Telescopic Handler all sizes including 360° slew': {
    displayNameOnCertificate: 'NPORS TELESCOPIC HANDLER ALL SIZES INCLUDING 360° SLEW\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED AN NPORS TELESCOPIC HANDLER ALL SIZES INCLUDING 360° SLEW TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL PLANT OPERATORS REGISTRATION SCHEME (NPORS)',
    validityMonths: 36,
    templateType: 'NPORS_TELESCOPIC_HANDLER',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Order Picker': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_ORDER_PICKER',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'Gent Peci'
  },
  'Tail Lift': {
    displayNameOnCertificate: 'LIFT TRUCK OPERATOR\nCERTIFICATE\nOF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH, CODE OF PRACTICE FOR RIDER OPERATED LIFT TRUCKS: OPERATOR TRAINING',
    validityMonths: 36,
    templateType: 'LIFT_TRUCK_TAIL_LIFT',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorName', 'instructorRegNumber'],
    imagePlaceholder: '/images/forklift-placeholder.png',
    defaultInstructor: 'Gent Peci'
  },
  'Face Fit Testing': {
    displayNameOnCertificate: 'FACE FIT TESTING\nCERTIFICATE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED FACE FIT TESTING FOR RESPIRATORY PROTECTIVE EQUIPMENT',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE HEALTH AND SAFETY AUTHORITY FOR THE USE OF RESPIRATORY PROTECTIVE EQUIPMENT IN THE WORKPLACE',
    validityMonths: 12,
    templateType: 'FACE_FIT_TESTING',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'Gent Peci'
  },
  'Safety Awareness Training for Shunter & Signalers': {
    displayNameOnCertificate: 'CERTIFICATE OF SAFETY AWARENESS TRAINING FOR SHUNTER & SIGNALERS',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED SITE SPECIFIC SAFETY AWARENESS TRAINING COURSE ON',
    additionalStaticText: [
      'TRAFFIC MANAGEMENT',
      'COUPLING & UN-COUPLING TRAILERS',
      'REVERSING OF ARTICULATED VEHICLES',
      'SHUNTING TRAILERS',
      'SIGNALING VEHICLES',
      'PEDESTRIAN MANAGEMENT',
      'LOADING OF VEHICLES'
    ],
    validityMonths: 36,
    templateType: 'SHUNTER_SIGNALERS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'Sean Gleeson'
  },
  'CSCS Mobile Tower Scaffold – Experienced': {
    displayNameOnCertificate: 'CSCS MOBILE TOWER SCAFFOLD\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A CSCS MOBILE TOWER SCAFFOLD TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH',
    validityMonths: 60,
    templateType: 'CSCS_MTS',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'CSCS Slinger Refresher': {
    displayNameOnCertificate: 'CSCS SLINGER REFRESHER\nCERTIFICATE OF COMPETENCY',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A CSCS SLINGER REFRESHER TRAINING COURSE',
    complianceStatement: 'THIS CERTIFICATE COMPLIES WITH THE RECOMMENDATIONS OF THE NATIONAL AUTHORITY FOR OCCUPATIONAL SAFETY AND HEALTH',
    validityMonths: 60,
    templateType: 'CSCS_SLINGER_REF',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Telescopic Handler / Teleporter Training': {
    displayNameOnCertificate: 'TELESCOPIC HANDLER / TELEPORTER\nCERTIFICATE OF TRAINING',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED SAFE USE AND OPERATION OF TELESCOPIC HANDLER (TELEPORTER) TRAINING',
    complianceStatement: 'TRAINING DELIVERED IN LINE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005, THE SAFETY, HEALTH AND WELFARE AT WORK (CONSTRUCTION) REGULATIONS 2013, AND THE SAFETY, HEALTH AND WELFARE AT WORK (GENERAL APPLICATION) REGULATIONS 2007',
    validityMonths: 36,
    templateType: 'TELESCOPIC_HANDLER_TELEPORTER',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'truckType', 'truckMake', 'truckCapacity', 'truckAttachments', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
  'Safe Loading & Unloading': {
    displayNameOnCertificate: 'SAFE LOADING & UNLOADING\nCERTIFICATE OF ATTENDANCE',
    completionStatement: 'HAS SUCCESSFULLY COMPLETED A SAFE LOADING & UNLOADING TRAINING COURSE',
    complianceStatement: 'AND HAS SUCCESSFULLY COMPLETED THIS COURSE IN ACCORDANCE WITH THE SAFETY, HEALTH AND WELFARE AT WORK ACT 2005',
    validityMonths: 36,
    templateType: 'SAFE_LOADING_UNLOADING',
    requiredFields: ['attendeeName', 'companyName', 'courseDate', 'instructorName', 'instructorRegNumber'],
    defaultInstructor: 'John Smith'
  },
} as const;